// Phase 3 — Copilot Session replay client.
//
// The reconnect-recovery fetch for the unified per-boat Copilot channel. The
// channel carries LIVE (ephemeral, no `seq`) events directly, but a dropped
// connection MISSES the gap; this helper replays the persisted LOGGED events
// (each carries a monotonic `seq`) with seq > {after}. after=0 = full history.
//
// Mirrors extractionRunsApi: tenant-scoped via the session cookie (no tenantId
// arg) and `tenantApi.getOne` unwraps the Laravel `{ data }` envelope. The
// endpoint returns `{ data: { events: [...] } }`, so we type the unwrap as
// `{ events }` and return the array.

import { tenantApi } from '@/modules/tenant/services/tenantApi';

/**
 * Every Copilot event type that can appear on the channel. Live-only types
 * (`field`/`entity`/`status`) never carry a `seq` and are never replayed;
 * logged types (`finding`/`step`/`insight`/`run_terminal`/`job`/`chat`) do.
 */
export type CopilotEventType =
  | 'field'
  | 'entity'
  | 'status'
  | 'finding'
  | 'step'
  | 'insight'
  | 'run_terminal'
  | 'job'
  | 'chat';

/**
 * The wire envelope (§4.1) — identical for the live publish and the replay
 * endpoint, so the client reducer handles both paths uniformly. `seq` is
 * present ONLY on logged events; its presence is the live-vs-logged
 * discriminator. Type-specific data lives in `payload`.
 */
export interface CopilotEventEnvelope {
  id: string;
  boat_id: string;
  seq?: number; // present only on logged events
  ts?: string;
  source: 'ai-engine' | 'laravel' | 'user';
  type: CopilotEventType;
  feature?: string;
  job_id?: string;
  epoch?: number;
  payload: Record<string, unknown>;
}

/**
 * Replay the persisted logged events for a boat with seq > {after} (in seq
 * order). after=0 returns the full history. The endpoint is tenant-scoped via
 * the session cookie; `getOne` unwraps the outer `{ data }` envelope.
 */
export async function fetchCopilotEvents(
  boatId: string,
  after = 0,
): Promise<CopilotEventEnvelope[]> {
  const { events } = await tenantApi.getOne<{ events: CopilotEventEnvelope[] }>(
    `/ai/copilot/${boatId}/events`,
    { params: { after } },
  );
  return events ?? [];
}
